/*
 *
 * Copyright (C) 2021 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

/* eslint-disable import/prefer-default-export */
import Category from 'enums/Category'
import LevelOneType, { LevelOneTypeStrings } from 'enums/LevelOneType'
import Measure from 'enums/Measure'
import Sentiment, { SentimentStrings } from 'enums/Sentiment'
import Gender, { GenderStrings } from 'enums/Gender'
import { SocialWebStoryScopeWidget } from 'schemas/SocialWebStoryScopeWidgetSchema'
import { getEmojiCategory, getInsightCategory } from 'utils/viewOptionUtil'
import { createInsightCount } from 'utils/runSearchUtil'
import { toFilter } from 'utils/widgetUtil'

const resolveLevelOneCategories = (widgetSetting: SocialWebStoryScopeWidget) => {
  const { viewOption, levelOneType } = widgetSetting

  switch (levelOneType) {
    case LevelOneType.NONE:
      return getInsightCategory(viewOption!)
    case LevelOneType.SENTIMENT:
    case LevelOneType.SENTIMENT_WO_NEUTRAL:
      return [Category.SENTIMENT]
    case LevelOneType.GENDER_WO_UNKNOWN:
      return [Category.GENDER]
    default:
      throw new Error(`Unknown Level One Type: ${levelOneType}`)
  }
}

const createSearchWithCategories = (
  widgetSetting: SocialWebStoryScopeWidget,
  dashboardId: number,
  name: string
) => {
  const options = {
    groupInfo: true,
    showPrimaryObject: widgetSetting.showPrimaryTerms,
    emojiCategory: getEmojiCategory(widgetSetting.viewOption)
  }

  return createInsightCount(
    dashboardId,
    name,
    widgetSetting.precision!,
    [widgetSetting.selectedTopic.topicId!],
    toFilter(widgetSetting),
    Measure.MENTIONS,
    resolveLevelOneCategories(widgetSetting),
    options
  )
}

const toFilterWithSentiment = (
  widgetSetting: SocialWebStoryScopeWidget,
  sentiment: SentimentStrings
) => {
  const filter = toFilter(widgetSetting)
  filter.additionalSentiments = [[sentiment]]

  return filter
}

const toFilterWithGender = (widgetSetting: SocialWebStoryScopeWidget, gender: GenderStrings) => {
  const filter = toFilter(widgetSetting)
  filter.genders = [gender]

  return filter
}

const resolveFilterWithLevelOneInsight = (
  widgetSetting: SocialWebStoryScopeWidget,
  insight: SentimentStrings | GenderStrings
) => {
  const { levelOneType } = widgetSetting
  switch (levelOneType) {
    case LevelOneType.SENTIMENT:
    case LevelOneType.SENTIMENT_WO_NEUTRAL:
      return toFilterWithSentiment(widgetSetting, insight as SentimentStrings)
    case LevelOneType.GENDER_WO_UNKNOWN:
      return toFilterWithGender(widgetSetting, insight as GenderStrings)
    default:
      throw new Error(`Unknown Level One Type: ${levelOneType}`)
  }
}

const createSearchWithInsightFilter = (
  widgetSetting: SocialWebStoryScopeWidget,
  insight: SentimentStrings | GenderStrings,
  dashboardId: number,
  dashboardName: string
) => {
  const options = {
    groupInfo: true,
    showPrimaryObject: widgetSetting.showPrimaryTerms,
    sizeNeeded: widgetSetting.topN! * 2,
    emojiCategory: getEmojiCategory(widgetSetting.viewOption)
  }

  return createInsightCount(
    dashboardId,
    dashboardName,
    widgetSetting.precision!,
    [widgetSetting.selectedTopic.topicId!],
    resolveFilterWithLevelOneInsight(widgetSetting, insight),
    Measure.MENTIONS,
    getInsightCategory(widgetSetting.viewOption!),
    options
  )
}

export const resolveLevelOneInsights = (levelOneType: LevelOneTypeStrings) => {
  switch (levelOneType) {
    case LevelOneType.NONE:
      return []
    case LevelOneType.SENTIMENT:
      return [Sentiment.POSITIVE, Sentiment.NEGATIVE, Sentiment.NEUTRAL]
    case LevelOneType.SENTIMENT_WO_NEUTRAL:
      return [Sentiment.POSITIVE, Sentiment.NEGATIVE]
    case LevelOneType.GENDER_WO_UNKNOWN:
      return [Gender.MALE, Gender.FEMALE]
    default:
      throw new Error(`Unknown Level One Type: ${levelOneType}`)
  }
}

export const createSearches = (
  widgetSetting: SocialWebStoryScopeWidget,
  dashboardId: number,
  dashboardName: string
) => {
  const { levelOneType } = widgetSetting
  return [
    createSearchWithCategories(widgetSetting, dashboardId, dashboardName),
    ...resolveLevelOneInsights(levelOneType!).map((insight) =>
      createSearchWithInsightFilter(widgetSetting, insight, dashboardId, dashboardName)
    )
  ]
}
